import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  useGetInvoiceHistoryByProjectId,
  useProjectReverseInvoiceMutation,
} from "../../../../api/services/project/projectApis";
import {
  MuiBox,
  MuiModal,
  MuiTypography,
  WarningConfirmationModal,
} from "../../../../components";
import { ManageRevenueSplit } from "../../../../components/manageRevenueSplit/ManageRevenueSplit";
import { STATUS_BADGE_TYPE } from "../../../../components/statusBadge/StatusBadge";
import {
  getAmountWithCurrency,
  replaceHashWithValue,
} from "../../../../helpers/utils";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import { AmountBadgeText } from "../../../../styles/common.style";
import { useExportInvoiceHistoryToExcel } from "../../../createProgram/hooks/useExportInvoiceHistoryToExcel";
import {
  ProjectInvoice,
  ProjectInvoiceItem,
  ProjectInvoiceReversalFormKey,
} from "../../modelsAndSchemas/Models";
import { ProjectInvoiceReverseValidationSchema } from "../../modelsAndSchemas/ValidationSchema";
import { projectInvoiceHistorySiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { ProjectInvoiceHistoryList } from "./components/projectInvoiceHistoryList/ProjectInvoiceHistoryLists";
import ProjectInvoiceReversal from "./components/projectInvoiceReversals/ProjectInvoiceReversal";
import { ViewProjectInvoice } from "./components/viewProjectInvoice/ViewProjectInvoiceDetails";
import { ProjectInvoiceHistoryWrapper } from "./ProjectInvoiceHistory.style";

interface ProjectInvoiceHistoryProps {
  projectId: string;
  isViewOnly?: boolean;
}

export const ProjectInvoiceHistory = ({
  projectId,
  isViewOnly,
}: ProjectInvoiceHistoryProps) => {
  const { commonLabels, fetchLabel, commonValidationMessages } = useSiteCore();
  const selectedProjectInvoiceRef = useRef<ProjectInvoice | null>(null);
  const getInvoiceHistoryLabel = (
    key: keyof typeof projectInvoiceHistorySiteCoreKeys,
  ) => {
    return fetchLabel(
      projectInvoiceHistorySiteCoreKeys.invoiceHistoryTitle,
      projectInvoiceHistorySiteCoreKeys[key],
    );
  };
  const [invoiceHistoryState, setInvoiceHistoryState] = useState<{
    showRevereConfirmationModal: boolean;
    showViewInvoice: boolean;
    selectedInvoiceId: string;
    selectedInvoiceNumber: string;
    selectedInvoiceStatus: string;
    showManageRevenueSplitModal: boolean;
    isMigratedRevenueSplits: boolean;
    isInvoiceReversed?: boolean;
  }>({
    showRevereConfirmationModal: false,
    showViewInvoice: false,
    selectedInvoiceId: "",
    selectedInvoiceNumber: "",
    selectedInvoiceStatus: "",
    showManageRevenueSplitModal: false,
    isMigratedRevenueSplits: false,
    isInvoiceReversed: false,
  });

  const methods = useForm({
    mode: "all",
    defaultValues: {
      [ProjectInvoiceReversalFormKey.projectReversalDate]: "",
      [ProjectInvoiceReversalFormKey.projectReversalReason]: "",
      [ProjectInvoiceReversalFormKey.projectComments]: "",
    },
    resolver: yupResolver(
      ProjectInvoiceReverseValidationSchema(commonValidationMessages),
    ),
  });

  const { handleSubmit, reset } = methods;

  const handleReversalSubmit = (data: Record<string, string | undefined>) => {
    reverseProjectInvoice({
      invoiceId: selectedProjectInvoiceRef.current?.id,
      date: dayjs().format(),
      reason: data[ProjectInvoiceReversalFormKey.projectReversalReason] ?? "",
      comments: data[ProjectInvoiceReversalFormKey.projectComments],
    });
    closeRevereConfirmation();
    reset();
  };

  const handleRevereAction = (selectedInvoice: ProjectInvoice) => {
    selectedProjectInvoiceRef.current = selectedInvoice;
    setInvoiceHistoryState((prevState) => ({
      ...prevState,
      showRevereConfirmationModal: true,
    }));
  };
  const closeRevereConfirmation = () => {
    reset();
    setInvoiceHistoryState((prevState) => ({
      ...prevState,
      showRevereConfirmationModal: false,
    }));
  };
  const handleDelete = handleSubmit(handleReversalSubmit);
  const closeMainModalHandler = () => {
    setInvoiceHistoryState((prevState) => ({
      ...prevState,
      showViewInvoice: false,
    }));
  };

  const showViewInvoiceModal = (selectedInvoice: ProjectInvoice) => {
    setInvoiceHistoryState((prevState) => ({
      ...prevState,
      showViewInvoice: true,
      selectedInvoiceId: selectedInvoice.id,
      selectedInvoiceNumber: selectedInvoice.number,
      selectedInvoiceStatus: selectedInvoice.statusId,
    }));
  };

  const manageRevenueSplitClick = (selectedInvoice: ProjectInvoiceItem) => {
    setInvoiceHistoryState((prevState) => ({
      ...prevState,
      showManageRevenueSplitModal: true,
      isMigratedRevenueSplits: false,
      selectedInvoiceId: selectedInvoice.id,
      selectedInvoiceNumber: selectedInvoice.number,
      selectedInvoiceStatus: selectedInvoice.statusId,
      isInvoiceReversed: selectedInvoice.isInvoiceReversed,
    }));
  };

  const handleViewRevenueSplitMigratedClick = (
    selectedInvoice: ProjectInvoiceItem,
  ) => {
    setInvoiceHistoryState((prevState) => ({
      ...prevState,
      showManageRevenueSplitModal: true,
      isMigratedRevenueSplits: true,
      selectedInvoiceId: selectedInvoice.id,
      selectedInvoiceNumber: selectedInvoice.number,
      selectedInvoiceStatus: selectedInvoice.statusId,
      isInvoiceReversed: selectedInvoice.isInvoiceReversed,
    }));
  };

  const {
    data: projectInvoiceHistory,
    refetchWithParams,
    isPending: isProjectInvoiceHistoryFetching,
  } = useGetInvoiceHistoryByProjectId();

  useEffect(() => {
    refetchWithParams(projectId);
  }, [projectId]);

  const {
    mutate: reverseProjectInvoice,
    isPending: isReverseProjectInvoicePending,
    isSuccess: isReverseProjectInvoiceSucceed,
  } = useProjectReverseInvoiceMutation();

  const dispatch = useDispatch();
  const { downloadExcel } = useExportInvoiceHistoryToExcel();

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          isReverseProjectInvoicePending || isProjectInvoiceHistoryFetching,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isReverseProjectInvoicePending, isProjectInvoiceHistoryFetching]);

  useEffect(() => {
    if (isReverseProjectInvoiceSucceed) {
      dispatch(
        toggleToast({
          open: true,
          message: getInvoiceHistoryLabel("invoiceReversed"),
        }),
      );
      refetchWithParams(projectId);
    }
  }, [isReverseProjectInvoiceSucceed]);

  const handleExportInvoice = async (selectedInvoice: ProjectInvoice) => {
    downloadExcel(selectedInvoice?.id, "project");
  };

  return (
    <ProjectInvoiceHistoryWrapper>
      <MuiBox className="total-invoice-amount-label">
        <MuiTypography variant="h3SemiBold">
          {getInvoiceHistoryLabel("totalInvoicedAmountAsOfToday")}{" "}
        </MuiTypography>
        <AmountBadgeText variant="h3Bold" className="nowrap-amount">
          {getAmountWithCurrency(
            projectInvoiceHistory?.totalAmount ?? 0,
            projectInvoiceHistory?.currency?.name ?? "",
          )}
        </AmountBadgeText>
      </MuiBox>
      {projectInvoiceHistory?.invoices && (
        <ProjectInvoiceHistoryList
          onReverseClick={handleRevereAction}
          onViewInvoiceClick={showViewInvoiceModal}
          onExportInvoiceClick={handleExportInvoice}
          onManageRevenueSplitClick={manageRevenueSplitClick}
          onViewRevenueSplitMigratedClick={handleViewRevenueSplitMigratedClick}
          data={projectInvoiceHistory?.invoices}
          isViewOnly={isViewOnly}
          currency={projectInvoiceHistory?.currency}
        />
      )}

      {invoiceHistoryState.showRevereConfirmationModal && (
        <WarningConfirmationModal
          labelContent={
            <FormProvider {...methods}>
              <ProjectInvoiceReversal
                invoiceId={selectedProjectInvoiceRef.current?.number}
              />
            </FormProvider>
          }
          modalTitle={commonLabels.confirmation}
          show={invoiceHistoryState.showRevereConfirmationModal}
          onClose={closeRevereConfirmation}
          onPrimaryButtonClick={handleDelete}
          primaryButtonTitle={commonLabels.yesProceed}
          secondaryButtonTitle={commonLabels.no}
        />
      )}
      {invoiceHistoryState.showViewInvoice && (
        <MuiModal
          open={invoiceHistoryState.showViewInvoice}
          onClose={closeMainModalHandler}
          title={replaceHashWithValue(
            getInvoiceHistoryLabel(
              invoiceHistoryState.selectedInvoiceStatus ===
                STATUS_BADGE_TYPE.invoiced
                ? "viewInvoiceHeader"
                : "viewReversedInvoiceHeader",
            ),
            invoiceHistoryState.selectedInvoiceNumber,
          )}
          secondaryButtonTitle={commonLabels.back}
          onSecondaryButtonClick={closeMainModalHandler}
          hasLargeContent
        >
          {invoiceHistoryState.selectedInvoiceId ? (
            <ViewProjectInvoice
              invoiceId={invoiceHistoryState.selectedInvoiceId}
              projectId={projectId}
            />
          ) : (
            <></>
          )}
        </MuiModal>
      )}

      {invoiceHistoryState.showManageRevenueSplitModal && (
        <ManageRevenueSplit
          isOpen={true}
          isViewOnly={isViewOnly}
          isMigratedRevenueSplits={invoiceHistoryState.isMigratedRevenueSplits}
          invoiceId={invoiceHistoryState.selectedInvoiceId}
          invoiceNumber={invoiceHistoryState.selectedInvoiceNumber}
          invoiceStatus={invoiceHistoryState.selectedInvoiceStatus}
          isInvoiceReversed={invoiceHistoryState.isInvoiceReversed}
          onClose={() => {
            let migratedValue: boolean;
            if (invoiceHistoryState.isMigratedRevenueSplits) {
              migratedValue = false;
            }
            setInvoiceHistoryState((prev) => ({
              ...prev,
              showManageRevenueSplitModal: false,
              isMigratedRevenueSplits:
                migratedValue ?? prev.isMigratedRevenueSplits,
            }));
          }}
          isForProject
        />
      )}
    </ProjectInvoiceHistoryWrapper>
  );
};
